import { FC, useEffect, useState } from 'react';
import { AI_IMAGE_GENERATION_TIPS } from 'app/modules/aiContent/constants/aiImageSuggestions';
import TimeUtils from 'app/utils/time';
import { useTranslation } from 'react-i18next';

type Props = {
  imageCount?: number;
};

const NINETY_SECONDS_DURATION = 90000;

const ALL_TIPS = AI_IMAGE_GENERATION_TIPS.categories.flatMap(
  (category) => category.tips,
);

const getFlashingColor = (index: number, elapsedTime: number) => {
  const colors = [
    '#2146EC',
    '#8746FF',
    '#647EF2',
    '#AB7EFF',
    '#90A3F6',
    '#C3A3FF',
    '#00C7E6',
  ];
  const colorIndex = Math.floor((elapsedTime / 1000 + index) % colors.length);
  return colors[colorIndex];
};

const getCircularProgress = (index: number, elapsedTime: number) => {
  const individualProgress = Math.min(
    (elapsedTime / 1000) * (1.1 + index * 0.1),
    99,
  );
  return individualProgress;
};

const getCurrentTip = (currentTipIndex: number) => {
  return ALL_TIPS[currentTipIndex];
};

const getCurrentTipCategory = (currentTipIndex: number) => {
  let tipCount = 0;
  for (const category of AI_IMAGE_GENERATION_TIPS.categories) {
    if (currentTipIndex < tipCount + category.tips.length) {
      return category.category;
    }
    tipCount += category.tips.length;
  }
  return AI_IMAGE_GENERATION_TIPS.categories[0].category;
};

const getProgressMessage = (progress: number, t: (key: string) => string) => {
  if (progress < 15) return t('aiImageSuggestions.loading.analyzingPrompt');
  if (progress < 30) return t('aiImageSuggestions.loading.processingImages');
  if (progress < 45) return t('aiImageSuggestions.loading.creatingVariations');
  if (progress < 60) return t('aiImageSuggestions.loading.applyingFilters');
  if (progress < 80) return t('aiImageSuggestions.loading.enhancingDetails');
  if (progress < 95) return t('aiImageSuggestions.loading.finalizingImages');
  return t('aiImageSuggestions.loading.almostReady');
};

const AiImageSuggestionsLoading: FC<Props> = ({ imageCount = 4 }) => {
  const { t } = useTranslation();
  const [elapsedTime, setElapsedTime] = useState(0);
  const [progress, setProgress] = useState(0);
  const [currentTipIndex, setCurrentTipIndex] = useState(0);

  useEffect(() => {
    const startTime = Date.now();
    const duration = NINETY_SECONDS_DURATION;

    const interval = setInterval(() => {
      const currentTime = Date.now();
      const elapsed = currentTime - startTime;
      const currentProgress = Math.min((elapsed / duration) * 99, 99);

      setElapsedTime(elapsed);
      setProgress(currentProgress);
    }, 100);

    const microProgressInterval = setInterval(() => {
      setProgress((prev) => {
        const randomIncrement = Math.random() * 0.5;
        return Math.min(prev + randomIncrement, 99);
      });
    }, 2000);

    const tipRotationInterval = setInterval(() => {
      setCurrentTipIndex((prev) => (prev + 1) % ALL_TIPS.length);
    }, 7000);

    return () => {
      clearInterval(interval);
      clearInterval(microProgressInterval);
      clearInterval(tipRotationInterval);
    };
  }, []);

  return (
    <div
      className="tw-relative tw-flex tw-flex-col tw-gap-4 tw-p-4"
      data-cy="ai-image-suggestions_loading"
    >
      <div
        className="tw-rounded-lg tw-border tw-border-grayscale-30 tw-bg-grayscale-5 tw-p-4"
        data-cy="ai-image-suggestions_loading_tip-card"
      >
        <div className="tw-mb-2 tw-flex tw-items-center tw-gap-2">
          <div className="tw-h-2 tw-w-2 tw-rounded-full tw-bg-primary-100" />
          <span
            className="tw-text-sm tw-font-medium tw-text-grayscale-180"
            data-cy="ai-image-suggestions_loading_tip-category"
          >
            {getCurrentTipCategory(currentTipIndex)}
          </span>
        </div>
        <p
          className="tw-text-base tw-leading-relaxed tw-text-grayscale-140 tw-transition-opacity tw-duration-500"
          data-cy="ai-image-suggestions_loading_tip-content"
        >
          {getCurrentTip(currentTipIndex)}
        </p>
      </div>

      <div
        className="tw-max-h-[75vh] tw-flex-grow tw-overflow-y-auto tw-pt-4"
        data-cy="ai-image-suggestions_loading_preview-grid"
      >
        <div className="tw-grid tw-grid-cols-2 tw-gap-4">
          {Array.from({ length: imageCount }).map((_, index) => (
            <div
              key={index}
              className="tw-relative tw-flex tw-aspect-square tw-items-center tw-justify-center tw-overflow-hidden tw-rounded tw-border tw-bg-white"
              style={{
                borderColor: getFlashingColor(index, elapsedTime),
              }}
              data-cy={`ai-image-suggestions_loading_preview-${index}`}
            >
              <div className="tw-absolute tw-inset-0 tw-rounded tw-bg-white" />

              <div
                className="tw-absolute tw-inset-0 tw-rounded"
                style={{
                  background: `linear-gradient(135deg, ${getFlashingColor(
                    index,
                    elapsedTime,
                  )}15, ${getFlashingColor(
                    index,
                    elapsedTime,
                  )}25, ${getFlashingColor(index, elapsedTime)}35)`,
                  animation: `softFlash 2s ease-in-out infinite ${
                    index * 0.3
                  }s`,
                }}
              />

              <div
                className="tw-absolute tw-inset-0"
                style={{
                  background: `linear-gradient(90deg, transparent, ${getFlashingColor(
                    index,
                    elapsedTime,
                  )}30, ${getFlashingColor(
                    index,
                    elapsedTime,
                  )}50, transparent)`,
                  animation: `shimmer 3s ease-in-out infinite ${index * 0.4}s`,
                  transform: 'translateX(-100%)',
                }}
              />

              <div className="tw-relative tw-z-10 tw-flex tw-items-center tw-justify-center">
                <div className="tw-relative tw-h-16 tw-w-16">
                  <svg
                    className="tw-absolute tw-inset-0 tw-h-full tw-w-full -tw-rotate-90 tw-transform"
                    viewBox="0 0 100 100"
                  >
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      stroke="#e4e7eb"
                      strokeWidth="8"
                      fill="transparent"
                    />
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      stroke="#2146EC"
                      strokeWidth="8"
                      fill="transparent"
                      strokeLinecap="round"
                      strokeDasharray={`${2 * Math.PI * 40}`}
                      strokeDashoffset={`${
                        2 *
                        Math.PI *
                        40 *
                        (1 - getCircularProgress(index, elapsedTime) / 100)
                      }`}
                      style={{
                        transition: 'stroke-dashoffset 0.3s ease',
                      }}
                    />
                  </svg>
                  <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center">
                    <span className="tw-text-xs tw-font-medium tw-text-grayscale-100">
                      {Math.round(getCircularProgress(index, elapsedTime))}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div
        className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-text-sm tw-text-grayscale-100"
        data-cy="ai-image-suggestions_loading_progress"
      >
        <div className="tw-flex tw-items-center tw-gap-1">
          <div className="tw-h-2 tw-w-2 tw-animate-pulse tw-rounded-full tw-bg-primary-100" />
          <span data-cy="ai-image-suggestions_loading_progress-message">
            {getProgressMessage(progress, t)}
          </span>
        </div>
        <span
          className="tw-font-medium tw-text-primary-100"
          data-cy="ai-image-suggestions_loading_elapsed-time"
        >
          {TimeUtils.formatMilliSeconds(elapsedTime)}
        </span>
      </div>

      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              transform: scale(1);
              opacity: 0.3;
            }
            50% {
              transform: scale(1.05);
              opacity: 0.6;
            }
          }
          @keyframes spark {
            0%, 100% {
              opacity: 0.2;
              transform: scale(0.8);
            }
            25% {
              opacity: 0.8;
              transform: scale(1.1);
            }
            50% {
              opacity: 1;
              transform: scale(1.2);
            }
            75% {
              opacity: 0.6;
              transform: scale(1.1);
            }
          }
          @keyframes softFlash {
            0%, 100% {
              opacity: 0.2;
            }
            50% {
              opacity: 0.6;
            }
          }
          @keyframes shimmer {
            0% {
              transform: translateX(-100%);
            }
            100% {
              transform: translateX(100%);
            }
          }
        `}
      </style>
    </div>
  );
};

export default AiImageSuggestionsLoading;
